// AddEdit.tsx

import {
  FormDatePicker,
  FormFileInput,
  FormInput,
  FormSelect,
  FormTextarea,
} from "@/components";
import useAddEdit from "./useAddEdit";
import { useState } from "react";
import { FormProvider } from "react-hook-form";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import {
  ASSIGN_TO,
  START_DATE,
  END_DATE,
  NAME,
  NOTES,
  PRIORITY,
  ROLE,
  SCHOOL,
  TASK_ATTACHMENT,
  TASK_ATTACHMENT_NAME,
  TASK_STATUS,
  TASKS,
  ASSIGNEES,
} from "../consts";
import { Trash2, X } from "lucide-react";
import { capitalize, cn } from "@/lib/utils";
import { PRIORITY_TYPES, roles } from "../consts";
import { taskStatus } from "@/lib/utils";
import { useGetSchoolsQuery, useGetPredefinedTasksQuery } from "@/services";
import { useProfilesByRoleQuery } from "@/services/employeeManagement";

import { LOGIN_ROLES } from "@/lib/utils";
import { useTaskCategoryQuery } from "@/services/masterData";
import { useQueries } from "@tanstack/react-query";
import { fetchEmployees } from "@/services/employeeManagement/useEmployeeManagementMutation";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const AddEdit = () => {
  const {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel,
    isMobile,
    action,
    isEdit,
    attachmentRef,
    onAddMoreClick,
    onRemoveTask,
    taskType,
    setTaskType,
    selectedPredefinedTasks,
    onSelectPredefinedTask,
    onRemovePredefinedTask,
    assignToOptions,
    handleAssignToChange,
    handleTaskTypeChange,
    onRemoveAssignField
  } = useAddEdit();

  const { errors } = form.formState;
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  

  // Fetch categories
  const { data: categoriesRes, isLoading: isLoadingCategories } = useTaskCategoryQuery({});
  const categories =
    categoriesRes?.data?.[0]?.records?.map((item: any) => ({
      label: item.name,
      value: item._id,
    })) || [];

  // Fetch predefined tasks based on selected category
  const { data: predefinedTasksData, isLoading: isPredefinedLoading } =
    useGetPredefinedTasksQuery(
      { page: pageNumber + 1, limit: pageSize, taskCategory: selectedCategory },
      { enabled: taskType === "predefined" && !!selectedCategory }
    );

  const predefinedTasks = predefinedTasksData?.data?.[0]?.records || [];

  const priorityOptions = Object.values(PRIORITY_TYPES).map((p) => ({
    label: p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
    value: p,
  }));

  const statusOptions = Object.values(taskStatus).map((s) => ({
    label: s.label,
    value: s.value,
  }));

 const {
     data: labsteamData,
     isLoading: isloadinglabsteam,
   } = useProfilesByRoleQuery(LOGIN_ROLES.LABS_TEAM);
    

   const labteamsOptions =
    labsteamData?.data?.[0]?.records?.map((profile: any) => ({
      label: profile.name,
      value: profile.profile_id,
    })) || [];
    
    const loggedInUserData = useSelector(
        (state: RootState) => state.currentData.loggedInUserData
      );
    
      // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
      const roles = loggedInUserData?.role || [];
    
      // Helper to check if user has a given role
      const hasRole = (role: string) => Array.isArray(roles) && roles.includes(role);

  return (
    <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
      <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
        <DrawerHeader className="px-0">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-[20px]">
              {capitalize(action || "")} Task Assignment
            </DrawerTitle>
            <X
              onClick={onCancel}
              className="cursor-pointer stroke-g-300 hover:stroke-g-400"
            />
          </div>
        </DrawerHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <div className="bg-white rounded-lg py-[25px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[100px] px-1">
              {/* Radio buttons for task type */}
              {/* Show radio buttons only when adding a new task */}
{!isEdit && (
  // Update the radio buttons to use the new handler
<div className="flex items-center gap-6 mb-6">
  <label className="flex items-center gap-2 cursor-pointer">
    <input
      type="radio"
      value="new"
      checked={taskType === "new"}
      onChange={() => handleTaskTypeChange("new")} // Use the new handler
      className="hidden peer"
    />
    <div
      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
        ${taskType === "new" ? "border-primary bg-primary" : "border-gray-300"}
      `}
    >
      {taskType === "new" && <div className="w-2 h-2 rounded-full bg-white" />}
    </div>
    <span className={`${taskType === "new" ? "text-primary font-medium" : "text-gray-700"}`}>
      New
    </span>
  </label>

  <label className="flex items-center gap-2 cursor-pointer">
    <input
      type="radio"
      value="predefined"
      checked={taskType === "predefined"}
      onChange={() => handleTaskTypeChange("predefined")} // Use the new handler
      className="hidden peer"
    />
    <div
      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
        ${taskType === "predefined" ? "border-primary bg-primary" : "border-gray-300"}
      `}
    >
      {taskType === "predefined" && <div className="w-2 h-2 rounded-full bg-white" />}
    </div>
    <span className={`${taskType === "predefined" ? "text-primary font-medium" : "text-gray-700"}`}>
      Pre-defined task
    </span>
  </label>
</div>
)}



              <div className="grid grid-cols-1 gap-[25px]">
  {taskType === "new" ? (
    form.watch(TASKS).map((_task: any, taskIndex: number) => (
      
      <div
        key={`tasks-${taskIndex}`}
        className={cn(
          "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[25px] border-b-1 py-5 border-primary"
        )}
      >
        {/* --- New Task Fields --- */}
                <div className="md:col-span-2">

        <FormInput
          name={`${TASKS}.${taskIndex}.${NAME}`}
          control={form.control}
          label="Task Name"
          placeholder="Please enter"
          error={(errors[TASKS] as any)?.[`${taskIndex}`]?.[NAME]?.message}
          isRequired={true}
          disabled={isView}
        />
</div>
        <FormSelect
          name={`${TASKS}.${taskIndex}.${PRIORITY}`}
          control={form.control}
          label="Priority"
          placeholder="Please select"
          error={(errors[TASKS] as any)?.[`${taskIndex}`]?.[PRIORITY]?.message}
          isRequired={true}
          disabled={isView}
          options={priorityOptions}
        />

        <FormDatePicker
          name={`${TASKS}.${taskIndex}.${START_DATE}`}
          control={form.control}
          label="Start Date"
          placeholder="Please select"
          disabled={isView || isEdit}
          isRequired={true}
        />
        <FormDatePicker
          name={`${TASKS}.${taskIndex}.${END_DATE}`}
          control={form.control}
          label="End Date"
          placeholder="Please select"
          disabled={isView || isEdit}
          isRequired={true}
          error={(errors[TASKS] as any)?.[`${taskIndex}`]?.[END_DATE]?.message}
        />

        <FormSelect
          name={`${TASKS}.${taskIndex}.${TASK_STATUS}`}
          control={form.control}
          label="Task Status"
          placeholder="Please select"
          disabled={isView}
          isRequired={true}
          options={statusOptions}
        />

        <FormFileInput
          fileInputRef={attachmentRef}
          name={`${TASKS}.${taskIndex}.${TASK_ATTACHMENT}`}
          control={form.control}
          label="Task Attachment"
          maxSize={5}
          disabled={isView}
          // isRequired={true}
          fileName={form.watch(`${TASKS}.${taskIndex}.${TASK_ATTACHMENT_NAME}`)} // ✅ This will display the existing file name
        />

        <div className="col-span-1 md:col-span-2 lg:col-span-3">
          <FormTextarea
            name={`${TASKS}.${taskIndex}.${NOTES}`}
            control={form.control}
            label="Task Notes"
            placeholder="Please enter"
            disabled={isView}
            isRequired={true}
            error={
                  (errors[TASKS] as any)?.[`${taskIndex}`]?.[NOTES]
                    ?.message
                }
          />
        </div>

        {/* Assignments Section */}
        <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-[#FFF5E9] gap-[25px] p-4">
  <div className="form-select-wrapper">
    {form.watch(`${TASKS}.0.${ASSIGNEES}`)?.map((_assign: any, assignIndex: number) => {
      // Get all currently selected assignTo values (excluding the current field)
      const currentAssignees = form.watch(`${TASKS}.0.${ASSIGNEES}`) || [];
      const selectedAssignToValues = currentAssignees
        .map((assignee: any, idx: number) => idx !== assignIndex ? assignee[ASSIGN_TO] : null)
        .filter((value: string | null) => value && value.trim() !== "");
      
      // Filter options to exclude already selected values
      const availableOptions = (hasRole(LOGIN_ROLES.LABS_TEAM_HEAD) || hasRole(LOGIN_ROLES.SUPER_ADMIN))
        ? labteamsOptions.filter((option: any) => 
            !selectedAssignToValues.includes(option.value) || 
            currentAssignees[assignIndex]?.[ASSIGN_TO] === option.value
          )
        : assignToOptions
            .filter((option) => 
              !selectedAssignToValues.includes(option.value) || 
              currentAssignees[assignIndex]?.[ASSIGN_TO] === option.value
            )
            .map((option) => ({
              value: option.value,
              label: option.label,
            }));

      return (
        <div
          key={`assign-0-${assignIndex}`}
          className="grid grid-cols-1 md:grid-cols-3 gap-[20px] bg-[#FFF5E9] p-3 rounded-md items-end"
        >
          {/* Assign To Field + Trash Icon (side by side) */}
          <div className="flex items-end gap-2 w-full">
            <div className="flex-1">
              <FormSelect
                name={`${TASKS}.0.${ASSIGNEES}.${assignIndex}.${ASSIGN_TO}`}
                control={form.control}
                label="Assign To"
                placeholder="Please select"
                error={
                  (errors[TASKS] as any)?.[0]?.[ASSIGNEES]?.[assignIndex]?.[ASSIGN_TO]?.message ||
                  (errors[TASKS] as any)?.[0]?.[ASSIGNEES]?.message
                }
                isRequired={true}
                options={availableOptions}
                onChange={(selectedValue: string) => {
                  handleAssignToChange(0, assignIndex, selectedValue);
                }}
                isLoading={
                  (hasRole(LOGIN_ROLES.LABS_TEAM_HEAD) || hasRole(LOGIN_ROLES.SUPER_ADMIN))
                    ? isloadinglabsteam
                    : ""
                }
              />
            </div>

            {/* Trash Icon Button */}
            {!isEdit && (
              <button
                type="button"
                onClick={() => onRemoveAssignField(0, assignIndex)}
                className="text-red-500 hover:text-red-700 mb-[6px]" // small adjust for alignment
                title="Remove"
              >
                <Trash2 size={20} />
              </button>
            )}
          </div>
        </div>
      );
    })}
  </div>

  {/* Add More Button */}
  {!isEdit && (
    <div className="mt-4 flex justify-end">
      <button
        type="button"
        onClick={() => onAddMoreClick(0)}
        className="text-primary hover:underline"
      >
        + Add More
      </button>
    </div>
  )}
</div>

        
      </div>
    ))
  ) : (
    // --- Predefined Task Shared Section ---
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[25px] border-b-1 py-5 border-primary">
      {/* Category */}
      <FormSelect
        name="selectedCategory"
        control={form.control}
        label="Category"
        placeholder="Please select"
        options={categories}
        isRequired={true}
        disabled={isView || isLoadingCategories}
        onChange={(val: string | undefined) => setSelectedCategory(val)}
        error={errors.selectedCategory?.message}
      />

      {/* Start / End Date */}
      <FormDatePicker
        name={`${TASKS}.0.${START_DATE}`}
        control={form.control}
        label="Start Date"
        placeholder="Please select"
        disabled={isView || isEdit}
        isRequired={true}
      />
      <FormDatePicker
        name={`${TASKS}.0.${END_DATE}`}
        control={form.control}
        label="End Date"
        placeholder="Please select"
        disabled={isView || isEdit}
        isRequired={true}
        error={(errors[TASKS] as any)?.[0]?.[END_DATE]?.message}
      />

      {/* Task Status */}
      <FormSelect
        name={`${TASKS}.0.${TASK_STATUS}`}
        control={form.control}
        label="Task Status"
        placeholder="Please select"
        disabled={isView}
        isRequired={true}
        options={statusOptions}
      />

      {/* Attachment */}
      <FormFileInput
        fileInputRef={attachmentRef}
        name={`${TASKS}.0.${TASK_ATTACHMENT}`}
        control={form.control}
        label="Task Attachment"
        maxSize={5}
        disabled={isView}
        // isRequired={true}
      />

      <div className="col-span-1 md:col-span-2 lg:col-span-3">
          <FormTextarea
            name={`${TASKS}.0.${NOTES}`}
            control={form.control}
            label="Task Notes"
            placeholder="Please enter"
            disabled={isView}
            isRequired={true}
            error={
                  (errors[TASKS] as any)?.[0]?.[NOTES]
                    ?.message
                }
          />
        </div>

      {/* Predefined Tasks Table */}
      {selectedCategory && (
        <div className="col-span-3">
          <div className="overflow-x-auto border border-gray-200 rounded-lg mt-3">
            <table className="min-w-full text-sm text-left text-gray-700">
              <thead className="bg-[#FFF5E9]">
                <tr>
                  <th className="px-4 py-3 font-semibold text-center">
                    <input
                      type="checkbox"
                      checked={
                        predefinedTasks.length > 0 &&
                        predefinedTasks.every((task: any) =>
                          selectedPredefinedTasks.some((t: any) => t._id === task._id)
                        )
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          predefinedTasks.forEach((task: any) => onSelectPredefinedTask(task));
                        } else {
                          predefinedTasks.forEach((task: any) => onRemovePredefinedTask(task));
                        }
                      }}
                    />
                  </th>
                  <th className="px-4 py-3 font-semibold">Task Name</th>
                  <th className="px-4 py-3 font-semibold">Priority</th>
                  <th className="px-4 py-3 font-semibold">Notes</th>
                  <th className="px-4 py-3 font-semibold text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {isPredefinedLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-3 text-center text-gray-400 italic">
                      Loading predefined tasks...
                    </td>
                  </tr>
                ) : predefinedTasks.length > 0 ? (
                  predefinedTasks.map((task: any, index: number) => {
                    const isSelected = selectedPredefinedTasks.some((t: any) => t._id === task._id);
                    return (
                      <tr
                        key={task._id || index}
                        className={cn(
                          "border-t hover:bg-gray-50 cursor-pointer",
                          isSelected ? "bg-blue-100" : ""
                        )}
                        onClick={() => onSelectPredefinedTask(task)}
                      >
                        <td
                          className="px-4 py-3 text-center"
                          onClick={(e) => e.stopPropagation()} // prevent row click
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                onSelectPredefinedTask(task);
                              } else {
                                onRemovePredefinedTask(task);
                              }
                            }}
                          />
                        </td>
                        <td className="px-4 py-3 text-primary">{task.taskName}</td>
                        <td className="px-4 py-3">{task.priority}</td>
                        <td className="px-4 py-3">{task.taskDescription || "-"}</td>
                        <td className="px-4 py-3 text-center">
                          {isSelected && (
                            <Trash2
                              size={18}
                              className="stroke-red-500 cursor-pointer hover:scale-110 transition-transform inline"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemovePredefinedTask(task);
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-3 text-center text-gray-400 italic">
                      No predefined tasks found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {selectedPredefinedTasks.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-gray-700 mt-3">
              <strong>Selected Tasks:</strong>
              <ul className="list-disc ml-5">
                {selectedPredefinedTasks.map((t) => (
                  <li key={t._id}>
                    {t.taskName} ({t.priority})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* --- Assignments Section (your full version) --- */}
      <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-[#FFF5E9] gap-[25px] p-4">
      <div className="form-select-wrapper">
        {form.watch(`${TASKS}.0.${ASSIGNEES}`)?.map((_assign: any, assignIndex: number) => {
          // Get all currently selected assignTo values (excluding the current field)
          const currentAssignees = form.watch(`${TASKS}.0.${ASSIGNEES}`) || [];
          const selectedAssignToValues = currentAssignees
            .map((assignee: any, idx: number) => idx !== assignIndex ? assignee[ASSIGN_TO] : null)
            .filter((value: string | null) => value && value.trim() !== "");
          
          // Filter options to exclude already selected values
          const availableOptions = (hasRole(LOGIN_ROLES.LABS_TEAM_HEAD) || hasRole(LOGIN_ROLES.SUPER_ADMIN))
            ? labteamsOptions.filter((option: any) => 
                !selectedAssignToValues.includes(option.value) || 
                currentAssignees[assignIndex]?.[ASSIGN_TO] === option.value
              )
            : assignToOptions
                .filter((option) => 
                  !selectedAssignToValues.includes(option.value) || 
                  currentAssignees[assignIndex]?.[ASSIGN_TO] === option.value
                )
                .map((option) => ({
                  value: option.value,
                  label: option.label,
                }));

          return (
            <div
              key={`assign-0-${assignIndex}`}
              className="grid grid-cols-1 md:grid-cols-3 gap-[20px] bg-[#FFF5E9] p-3 rounded-md items-end"
            >
              {/* Assign To Field + Trash Icon (side by side) */}
              <div className="flex items-end gap-2 w-full">
                <div className="flex-1">
                  <FormSelect
                    name={`${TASKS}.0.${ASSIGNEES}.${assignIndex}.${ASSIGN_TO}`}
                    control={form.control}
                    label="Assign To"
                    placeholder="Please select"
                    error={
                      (errors[TASKS] as any)?.[0]?.[ASSIGNEES]?.[assignIndex]?.[ASSIGN_TO]?.message ||
                      (errors[TASKS] as any)?.[0]?.[ASSIGNEES]?.message
                    }
                    isRequired={true}
                    options={availableOptions}
                    onChange={(selectedValue: string) => {
                      handleAssignToChange(0, assignIndex, selectedValue);
                    }}
                    isLoading={
                      (hasRole(LOGIN_ROLES.LABS_TEAM_HEAD) || hasRole(LOGIN_ROLES.SUPER_ADMIN))
                        ? isloadinglabsteam
                        : ""
                    }
                  />
                </div>

                {/* Trash Icon Button */}
                {!isEdit && (
                  <button
                    type="button"
                    onClick={() => onRemoveAssignField(0, assignIndex)}
                    className="text-red-500 hover:text-red-700 mb-[6px]" // small adjust for alignment
                    title="Remove"
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add More Button */}
      {!isEdit && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => onAddMoreClick(0)}
            className="text-primary hover:underline"
          >
            + Add More
          </button>
        </div>
      )}
    </div>
    </div>
  )}
</div>

            </div>

            <div className="flex flex-row justify-end gap-3 py-3 absolute bottom-5 w-full bg-white pb-[25px] z-[3]">
              <Button variant="outline" type="button" onClick={onCancel}>
                Cancel
              </Button>
              {isView ? null : (
                <Button type="submit">{isEdit ? "Update" : "Submit"}</Button>
              )}
            </div>
          </form>
        </FormProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default AddEdit;